import { SearchState } from '../reducers/search/searchTypes';
import { UserState } from '../reducers/user/userTypes';
import { isBusinessOperation } from './urlHelper';

export interface ConditionalFormValues {
  postCode: string;
  state: string;
  city: string;
  address1: string;
  address2: string;
  customerNumber: string;
  updateProfile?: boolean;
}

const isSearchedPostCodeSameAsUserPostCode = (
  search: SearchState,
  user: UserState,
): boolean => {
  return user.isLoggedIn && search.postCode === user.postCode;
};

const isPostCodeFromURL = (disabledPrevButton: string): boolean =>
  !!disabledPrevButton;

const getUserValues = (
  user: UserState,
  includeCustomerNbr: boolean,
): ConditionalFormValues => {
  return {
    postCode: user.postCode,
    state: user.state || '',
    city: user.city,
    address1: user.streetAddress,
    address2: user.streetAddress2 || '',
    customerNumber: includeCustomerNbr ? user.customerNumber || '' : '',
  };
};

const getSearchValues = (search: SearchState): ConditionalFormValues => {
  return {
    postCode: search.postCode,
    state: search.state || '',
    city: search.city || '',
    address1: search.address || '',
    address2: '',
    customerNumber: '',
    updateProfile: false,
  };
};

const getMixedValues = (
  user: UserState,
  search: SearchState,
  includeCustomerNbr: boolean,
): ConditionalFormValues => {
  return {
    postCode: search.postCode,
    state: search.state || user.state || '',
    city: search.city || user.city,
    address1: search.address || user.streetAddress,
    address2: user.streetAddress2 || '',
    customerNumber: includeCustomerNbr ? user.customerNumber || '' : '',
  };
};

export const getConditionalFormValues = (
  user: UserState,
  search: SearchState,
  operation: string,
  disabledPrevButton: string,
  showMinimalForm: boolean,
  appConfig: typeof CSS_CONFIG,
): ConditionalFormValues => {
  // Logic documented here:
  // https://confluence.build.ingka.ikea.com/display/MSS/Contact+form#Contactform-Autofilllogic
  // Please update the documentation when changes has been done to this area.
  const isBusinessOp = isBusinessOperation(operation, appConfig);
  const includeCustomerNbr =
    !isBusinessOp || (user.customerType === 'Business' && isBusinessOp);

  if (isPostCodeFromURL(disabledPrevButton)) {
    if (showMinimalForm) {
      return getSearchValues(search);
    }
    return getUserValues(user, includeCustomerNbr);
  }

  if (user.isLoggedIn) {
    return isSearchedPostCodeSameAsUserPostCode(search, user)
      ? getMixedValues(user, search, includeCustomerNbr)
      : getSearchValues(search);
  }

  return getSearchValues(search);
};

export const isMinimalFormPreconditionsMet = (
  isPaidService: boolean,
  isAtHomeService: boolean,
  isLoggedIn: boolean,
  isMinimalFormEnabled: boolean,
): boolean => {
  return (
    !isPaidService && !isAtHomeService && !isLoggedIn && isMinimalFormEnabled
  );
};

export const replaceSpaceChar = (input: string): string => {
  const whitespaceRegex = /\s+/g;
  return input.replace(whitespaceRegex, '');
};
